import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, Users, Trophy, Star, Ship, Anchor, Compass, Plus, Link } from 'lucide-react';
import GroupCompetitionDashboard from './GroupCompetitionDashboard';

// Types
interface TicketClass {
  key: 'third' | 'second' | 'first';
  name: string;
  icon: string;
  stakeRange: string;
  features: string[];
  color: string;
  gradient: string;
}

interface Group {
  id: number;
  name: string;
  members: number;
  totalStake: number;
  class: string;
  avatar: string;
  category: string;
  status: 'forming' | 'ready' | 'in-progress';
}

interface GroupCompetitionModalProps {
  onClose: () => void;
  currentUserId?: string;
}

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const wave = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 90vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const TitleIcon = styled.span`
  font-size: 2.5rem;
  animation: ${float} 3s ease-in-out infinite;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  font-size: 14px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

// Ticket Class Selection
const TicketSelectionSection = styled.div<{ isVisible: boolean }>`
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: all 0.5s ease;
  display: ${props => props.isVisible ? 'block' : 'none'};
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 20px;
  text-align: center;
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 30px;
  line-height: 1.6;
`;

const TicketClassesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 30px;
`;

const TicketClassCard = styled.div<{ isSelected: boolean; gradient: string }>`
  background: ${props => props.isSelected 
    ? props.gradient 
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid ${props => props.isSelected 
    ? 'rgba(255, 255, 255, 0.3)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border-radius: 20px;
  padding: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  ${props => props.isSelected && `
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  `}
`;

const TicketClassHeader = styled.div`
  text-align: center;
  margin-bottom: 25px;
`;

const TicketClassIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 15px;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const TicketClassName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 10px;
`;

const TicketClassStake = styled.div`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin-bottom: 20px;
`;

const TicketClassFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TicketClassFeature = styled.li`
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #4caf50;
    font-weight: bold;
  }
`;

// Groups Display
const GroupsSection = styled.div<{ isVisible: boolean }>`
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: all 0.5s ease;
  display: ${props => props.isVisible ? 'block' : 'none'};
`;

const GroupsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 15px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const GroupsTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const GroupsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
`;

const GroupCard = styled.div<{ status: string }>`
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid ${props => {
    if (props.status === 'ready') return '#4caf50';
    if (props.status === 'in-progress') return '#ff9800';
    return 'rgba(255, 255, 255, 0.2)';
  }};
  border-radius: 20px;
  padding: 25px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const GroupAvatar = styled.div<{ class: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => {
    if (props.class === 'first') return 'linear-gradient(135deg, #ffd700, #ffed4e)';
    if (props.class === 'second') return 'linear-gradient(135deg, #c0c0c0, #e5e5e5)';
    return 'linear-gradient(135deg, #cd7f32, #daa520)';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  color: ${props => props.class === 'second' ? '#333' : 'white'};
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const GroupInfo = styled.div`
  flex: 1;
`;

const GroupName = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: white;
  margin: 0 0 5px 0;
`;

const GroupCategory = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 5px;
`;

const GroupStats = styled.div`
  display: flex;
  gap: 15px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

const GroupStatus = styled.div<{ status: string }>`
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    if (props.status === 'ready') return 'rgba(76, 175, 80, 0.2)';
    if (props.status === 'in-progress') return 'rgba(255, 152, 0, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  color: ${props => {
    if (props.status === 'ready') return '#4caf50';
    if (props.status === 'in-progress') return '#ff9800';
    return 'rgba(255, 255, 255, 0.8)';
  }};
  border: 1px solid ${props => {
    if (props.status === 'ready') return '#4caf50';
    if (props.status === 'in-progress') return '#ff9800';
    return 'rgba(255, 255, 255, 0.2)';
  }};
`;

const GroupActions = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const JoinButton = styled.button`
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 10px 25px rgba(76, 175, 80, 0.4);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.2);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ViewButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

// Ocean wave effect
const WaveLayer = styled.div<{ delay: number; speed: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: ${wave} ${props => props.speed}s infinite linear;
  animation-delay: ${props => props.delay}s;
  opacity: 0.6;
  z-index: -1;
`;

const GroupCompetitionModal: React.FC<GroupCompetitionModalProps> = ({ onClose, currentUserId = 'default-user' }) => {
  const [selectedTicketClass, setSelectedTicketClass] = useState<"third" | "second" | "first" | null>(null);
  const [groupCompetitors, setGroupCompetitors] = useState<Group[]>([]);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showTicketInput, setShowTicketInput] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [ticketLink, setTicketLink] = useState('');

  // Ticket class definitions
  const ticketClasses: TicketClass[] = [
    {
      key: 'third',
      name: 'Third Class',
      icon: '🥉',
      stakeRange: 'KES 1-499',
      features: [
        'Multiple Choice Questions',
        'Short Answer Questions',
        'Image/Pattern Identification',
        'Basic Problem Solving',
        'Standard Support'
      ],
      color: '#cd7f32',
      gradient: 'linear-gradient(135deg, #cd7f32, #daa520)'
    },
    {
      key: 'second',
      name: 'Second Class',
      icon: '🥈',
      stakeRange: 'KES 500-999',
      features: [
        'All Third Class Features',
        'Complex Problem Solving',
        'Essay Questions',
        'Advanced Analytics',
        'Priority Support',
        'Extended Time Limits'
      ],
      color: '#c0c0c0',
      gradient: 'linear-gradient(135deg, #c0c0c0, #e5e5e5)'
    },
    {
      key: 'first',
      name: 'First Class',
      icon: '🥇',
      stakeRange: 'KES 1000+',
      features: [
        'All Previous Features',
        'Advanced Analytics',
        'Priority Support',
        'Exclusive Content',
        'Custom Difficulty',
        'VIP Experience',
        'Extended Support Hours'
      ],
      color: '#ffd700',
      gradient: 'linear-gradient(135deg, #ffd700, #ffed4e)'
    }
  ];

  // Handle ticket class selection
  const selectTicketClass = (ticketClass: "third" | "second" | "first") => {
    setSelectedTicketClass(ticketClass);
    
    // Generate sample groups based on selected class
    const sampleGroups: Group[] = [
      {
        id: 1,
        name: "Mathematics Masters",
        members: ticketClass === "first" ? 8 : ticketClass === "second" ? 6 : 4,
        totalStake: ticketClass === "first" ? 12000 : ticketClass === "second" ? 6000 : 2400,
        class: ticketClass,
        avatar: "MM",
        category: "Mathematics",
        status: 'forming'
      },
      {
        id: 2,
        name: "Science Squad",
        members: ticketClass === "first" ? 6 : ticketClass === "second" ? 5 : 4,
        totalStake: ticketClass === "first" ? 9000 : ticketClass === "second" ? 4500 : 1800,
        class: ticketClass,
        avatar: "SS",
        category: "Science",
        status: 'ready'
      },
      {
        id: 3,
        name: "History Heroes",
        members: ticketClass === "first" ? 7 : ticketClass === "second" ? 5 : 3,
        totalStake: ticketClass === "first" ? 10500 : ticketClass === "second" ? 5250 : 2100,
        class: ticketClass,
        avatar: "HH",
        category: "History",
        status: 'forming'
      },
      {
        id: 4,
        name: "Literature Legends",
        members: ticketClass === "first" ? 5 : ticketClass === "second" ? 4 : 3,
        totalStake: ticketClass === "first" ? 7500 : ticketClass === "second" ? 3750 : 1500,
        class: ticketClass,
        avatar: "LL",
        category: "Literature",
        status: 'in-progress'
      }
    ];
    
    setGroupCompetitors(sampleGroups);
  };

  // Handle group joining (placeholder for future implementation)
  const handleJoinGroup = (groupId: number) => {
    console.log(`Joining group ${groupId} with ticket class ${selectedTicketClass}`);
    
    // Find the selected group
    const group = groupCompetitors.find(g => g.id === groupId);
    if (!group) {
      console.error('Group not found');
      return;
    }
    
    // Check if group is available for joining
    if (group.status === 'in-progress') {
      alert('This group competition is already in progress and cannot be joined.');
      return;
    }
    
    // Set the selected group and show the lobby
    setSelectedGroup(group);
    setShowTicketInput(true);
  };

  // Handle joining the competition from the lobby
  const handleJoinCompetition = (groupId: number) => {
    console.log(`User joined competition for group ${groupId} with ticket: ${ticketLink}`);
    
    // Validate ticket link format
    if (!ticketLink.includes('lavernai.com/ticket/')) {
      alert('Please enter a valid LavernAI ticket link');
      return;
    }
    
    // Here you would typically:
    // - Validate the ticket link with the backend
    // - Check if the ticket is valid and unused
    // - Verify user has sufficient balance
    // - Add user to the group
    // - Update group status
    // - Redirect to competition interface
    
    // For now, just show a success message and close the ticket input
    alert('Successfully joined the group! The competition will begin when enough members join.');
    setShowTicketInput(false);
    setSelectedGroup(null);
    setTicketLink('');
  };

  // Handle withdrawal from group
  const handleWithdraw = (groupId: number, reason: string, customReason?: string) => {
    console.log(`User withdrew from group ${groupId}, reason: ${reason}, custom: ${customReason}`);
    // Here you would typically:
    // - Remove user from group
    // - Process refund if applicable
    // - Update group status
    
    alert('Successfully withdrew from the group.');
    setShowTicketInput(false);
    setSelectedGroup(null);
    setTicketLink('');
  };

  // Handle dashboard view
  const handleViewDashboard = () => {
    setShowDashboard(true);
  };

  // Handle group viewing (placeholder for future implementation)
  const handleViewGroup = (groupId: number) => {
    console.log(`Viewing group ${groupId} details`);
    // TODO: Implement group viewing logic
    // - Show group details
    // - Display member list
    // - Show competition rules
  };

  // Reset to ticket selection
  const handleBackToTickets = () => {
    setSelectedTicketClass(null);
    setGroupCompetitors([]);
  };

  if (showDashboard) {
    return (
      <GroupCompetitionDashboard
        currentUserId={currentUserId}
        onClose={() => setShowDashboard(false)}
      />
    );
  }

  if (showTicketInput && selectedGroup) {
    return (
      <ModalOverlay>
        <Header>
          <Title>
            <TitleIcon>🚢</TitleIcon>
            Join {selectedGroup.name}
          </Title>
          <CloseButton onClick={() => {
            setShowTicketInput(false);
            setSelectedGroup(null);
            setTicketLink('');
          }}>
            <X size={18} />
            Close
          </CloseButton>
        </Header>

        <ContentArea>
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.1)', 
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '30px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '20px', color: '#ffd700' }}>
              🎫 Enter Your Ticket Link
            </h2>
            <p style={{ marginBottom: '30px', opacity: 0.9 }}>
              Paste your ticket link below to join this group competition. 
              Make sure you have sufficient balance for the {selectedGroup.class} class.
            </p>
            
            <div style={{ 
              background: 'rgba(255, 215, 0, 0.1)', 
              border: '1px solid rgba(255, 215, 0, 0.3)',
              borderRadius: '15px',
              padding: '15px',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#ffd700' }}>
                <strong>💡 How to get your ticket link:</strong>
              </p>
              <ol style={{ margin: '0', paddingLeft: '20px', fontSize: '0.9rem', opacity: 0.8 }}>
                <li>Purchase a ticket from the Ticket Purchase section</li>
                <li>Copy the generated ticket link</li>
                <li>Paste it here to join the group</li>
              </ol>
            </div>

            <div style={{ marginBottom: '30px', position: 'relative' }}>
              <input
                type="text"
                placeholder="Paste your ticket link here..."
                value={ticketLink}
                onChange={(e) => setTicketLink(e.target.value)}
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  paddingRight: '60px',
                  borderRadius: '25px',
                  border: ticketLink.includes('lavernai.com/ticket/') 
                    ? '2px solid #10b981' 
                    : ticketLink.length > 0 
                    ? '2px solid #ef4444' 
                    : '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
              />
              <button
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText();
                    setTicketLink(text);
                  } catch (err) {
                    console.error('Failed to read clipboard:', err);
                    alert('Please paste your ticket link manually');
                  }
                }}
                style={{
                  position: 'absolute',
                  right: '5px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '8px',
                  cursor: 'pointer',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Paste from clipboard"
              >
                <Link size={16} />
              </button>
            </div>
            
            {/* Ticket link validation feedback */}
            {ticketLink.length > 0 && (
              <div style={{ 
                marginBottom: '20px',
                padding: '10px 15px',
                borderRadius: '10px',
                background: ticketLink.includes('lavernai.com/ticket/') 
                  ? 'rgba(16, 185, 129, 0.1)' 
                  : 'rgba(239, 68, 68, 0.1)',
                border: '1px solid',
                borderColor: ticketLink.includes('lavernai.com/ticket/') 
                  ? 'rgba(16, 185, 129, 0.3)' 
                  : 'rgba(239, 68, 68, 0.3)',
                color: ticketLink.includes('lavernai.com/ticket/') 
                  ? '#10b981' 
                  : '#ef4444',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {ticketLink.includes('lavernai.com/ticket/') ? (
                  <>
                    <span>✅</span>
                    Valid ticket link format
                  </>
                ) : (
                  <>
                    <span>❌</span>
                    Please enter a valid LavernAI ticket link
                  </>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  if (!ticketLink.trim()) {
                    alert('Please enter your ticket link');
                    return;
                  }
                  handleJoinCompetition(selectedGroup.id);
                }}
                disabled={!ticketLink.includes('lavernai.com/ticket/')}
                style={{
                  background: ticketLink.includes('lavernai.com/ticket/')
                    ? 'linear-gradient(45deg, #ffd700, #ffed4e)'
                    : 'rgba(255, 255, 255, 0.1)',
                  color: ticketLink.includes('lavernai.com/ticket/') ? '#1e293b' : 'rgba(255, 255, 255, 0.5)',
                  border: 'none',
                  padding: '15px 30px',
                  borderRadius: '25px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  cursor: ticketLink.includes('lavernai.com/ticket/') ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  opacity: ticketLink.includes('lavernai.com/ticket/') ? 1 : 0.6
                }}
              >
                <Link size={20} />
                Join Group
              </button>
              
              <button
                onClick={() => {
                  setShowTicketInput(false);
                  setSelectedGroup(null);
                  setTicketLink('');
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  padding: '15px 30px',
                  borderRadius: '25px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </ContentArea>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay>
      {/* Ocean wave effects */}
      <WaveLayer delay={0} speed={4} />
      <WaveLayer delay={1} speed={5} />
      <WaveLayer delay={2} speed={6} />
      
      <Header>
        <Title>
          <TitleIcon>🚢</TitleIcon>
          LAVERN TITAN - Group Competition
        </Title>
        <CloseButton onClick={onClose}>
          <X size={18} />
          Close
        </CloseButton>
      </Header>

      <ContentArea>
        {/* Ticket Class Selection */}
        <TicketSelectionSection isVisible={!selectedTicketClass}>
          <SectionTitle>Choose Your Ticket Class</SectionTitle>
          <SectionDescription>
            Select your ticket class to join group competitions. Higher classes offer more features and exclusive experiences.
          </SectionDescription>
          
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <button
              onClick={handleViewDashboard}
              style={{
                background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                color: '#1e293b',
                border: 'none',
                padding: '15px 30px',
                borderRadius: '25px',
                fontSize: '1.1rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                margin: '0 auto',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 15px 30px rgba(255, 215, 0, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Plus size={20} />
              View All Group Competitions
            </button>
          </div>
          
          <TicketClassesGrid>
            {ticketClasses.map((ticketClass) => (
              <TicketClassCard
                key={ticketClass.key}
                isSelected={false}
                gradient={ticketClass.gradient}
                onClick={() => selectTicketClass(ticketClass.key)}
              >
                <TicketClassHeader>
                  <TicketClassIcon>{ticketClass.icon}</TicketClassIcon>
                  <TicketClassName>{ticketClass.name}</TicketClassName>
                  <TicketClassStake>{ticketClass.stakeRange}</TicketClassStake>
                </TicketClassHeader>
                
                <TicketClassFeatures>
                  {ticketClass.features.map((feature, index) => (
                    <TicketClassFeature key={index}>{feature}</TicketClassFeature>
                  ))}
                </TicketClassFeatures>
              </TicketClassCard>
            ))}
          </TicketClassesGrid>
        </TicketSelectionSection>

        {/* Available Groups Display */}
        <GroupsSection isVisible={!!selectedTicketClass}>
          <GroupsHeader>
            <BackButton onClick={handleBackToTickets}>
              <Anchor size={16} />
              Back to Tickets
            </BackButton>
            <GroupsTitle>
              {selectedTicketClass && (
                <>
                  {ticketClasses.find(tc => tc.key === selectedTicketClass)?.icon} 
                  {ticketClasses.find(tc => tc.key === selectedTicketClass)?.name} Groups
                  <span style={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.7)', marginLeft: '10px' }}>
                    ({ticketClasses.find(tc => tc.key === selectedTicketClass)?.stakeRange})
                  </span>
                </>
              )}
            </GroupsTitle>
          </GroupsHeader>

          <GroupsList>
            {groupCompetitors.map((group) => (
              <GroupCard key={group.id} status={group.status}>
                <GroupHeader>
                  <GroupAvatar class={group.class}>{group.avatar}</GroupAvatar>
                  <GroupInfo>
                    <GroupName>{group.name}</GroupName>
                    <GroupCategory>{group.category}</GroupCategory>
                    <GroupStats>
                      <span>👥 {group.members} members</span>
                      <span>💰 KES {group.totalStake.toLocaleString()}</span>
                    </GroupStats>
                  </GroupInfo>
                  <GroupStatus status={group.status}>
                    {group.status === 'ready' ? 'Ready' : 
                     group.status === 'in-progress' ? 'In Progress' : 'Forming'}
                  </GroupStatus>
                </GroupHeader>
                
                <GroupActions>
                  <JoinButton 
                    onClick={() => handleJoinGroup(group.id)}
                    disabled={group.status === 'in-progress'}
                  >
                    {group.status === 'in-progress' ? 'In Progress' : 'Join Group'}
                  </JoinButton>
                  <ViewButton onClick={() => handleViewGroup(group.id)}>
                    View Details
                  </ViewButton>
                </GroupActions>
              </GroupCard>
            ))}
          </GroupsList>
        </GroupsSection>
      </ContentArea>
    </ModalOverlay>
  );
};

export default GroupCompetitionModal;
